import { useState, useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  createColumn,
  createViewAction,
  getRegister,
  formatDateTime,
  createEditAction,
  numberFormat,
} from "../../utils/helpers";
import { URL_ADVANCES_BALANCE } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_ADVANCES_BALANCE } from "../../utils/apis";
import { PrimaryButton } from "../../components/common/Buttons";
import { LinkCarDialog } from "../../components/advances_balance/LinkCarDialog";
import useSet from "../common/useSet";
import { useToasts } from "../../hooks/common/useToasts";
import { useParams } from "react-router-dom";
import { Tag } from "antd";
import { ConfirmModal } from "../../components/common/Modals";
import { BooleanTags } from "../../components/common/registerUtils";

export const useAdvancesBalance = () => {
  const { id } = useParams<{ id: string }>();
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [selectedCarNumber, setSelectedCarNumber] = useState<string | null>(
    null,
  );

  const { uploadData: onLinkCar, success, error } = useSet();

  useToasts({
    success,
    error,
    redirect: "reload",
    successMessage: "Operation Successfull",
  });

  const handleOpenLinkDialog = () => {
    setIsLinkDialogOpen(true);
  };

  const handleCloseLinkDialog = () => {
    setIsLinkDialogOpen(false);
  };

  const handleLinkCar = async (plateNumbers: string[]) => {
    if (!id) {
      return;
    }

    onLinkCar({
      api: `advances/${id}/link_car/`,
      method: "POST",
      params: { car_number: plateNumbers },
    });
    handleCloseLinkDialog();
  };

  const handleUnlinkCar = (carNumber: string) => {
    if (!id || !carNumber) return;

    setSelectedCarNumber(carNumber);
    setShowUnlinkModal(true);
  };

  const confirmUnlinkCar = async () => {
    if (!id || !selectedCarNumber) return;

    setShowUnlinkModal(false);

    onLinkCar({
      api: `advances/${id}/unlink_car/`,
      method: "POST",
      params: { car_number: selectedCarNumber },
    });

    setSelectedCarNumber(null);
  };

  const tableColumns = () => [
    createColumn("phone_number", true, "Phone Number"),
    createColumn("prepayment_total", false, "Prepaid Amount"),
    createColumn("balance", false, "Balance"),
    createColumn("date", { type: "date" }),
    createColumn("is_active", true, "Status"),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      return {
        ...item,
        balance: numberFormat(item?.balance || 0),
        prepayment_total: numberFormat(item?.prepayment_total || 0),
        date: formatDateTime(item?.created_at),
        is_active: <BooleanTags status={item?.is_active} />,
        actions: (
          <FormActions
            actions={[
              createViewAction(URL_ADVANCES_BALANCE, item?.id),
              createEditAction(URL_ADVANCES_BALANCE, item?.id),
              // createDeleteAction(URL_ADVANCES_BALANCE, item?.id),
            ]}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }

    const handleLinkCar = () => {
      handleOpenLinkDialog();
    };

    return {
      sections: [
        {
          title: "E-Coupon Balance Details",
          action: <PrimaryButton title="Link Cars" onClick={handleLinkCar} />,
          fields: {
            phone_number: item?.phone_number || "N/A",
            balance: item?.balance ? item.balance : "UGX 0",
            car_numbers: item?.car_numbers
              ? item?.car_numbers.map((car_number: string) => (
                  <Tag
                    key={car_number}
                    onClick={() => handleUnlinkCar(car_number)}
                    style={{ cursor: "pointer" }}
                    color="blue"
                  >
                    {car_number}
                  </Tag>
                ))
              : "N/A",
            is_active: <BooleanTags status={item?.is_active} />,
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item?.id || "N/A",
            created_by: item?.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`.trim() ||
                "System"
              : "System",
            created_at: item?.created_at
              ? formatDateTime(item.created_at)
              : "N/A",
            updated_at: item?.updated_at
              ? formatDateTime(item.updated_at)
              : "N/A",
            updated_by: item?.updated_by
              ? `${item.updated_by.first_name} ${item.updated_by.last_name}`.trim() ||
                "Not updated"
              : "Not updated",
          },
        },
      ],
    };
  };

  // const formRoute = getForm(URL_ADVANCES_BALANCE);
  const activityFilter = "advances";
  const tableTitle = "E-Coupon Balance";

  return {
    tableData,
    tableColumns,
    tableTitle,
    viewData,
    formFields: [],
    formActions: [],
    defaultValues: {},
    linkCarDialog: (
      <>
        <LinkCarDialog
          open={isLinkDialogOpen}
          onClose={handleCloseLinkDialog}
          onSubmit={handleLinkCar}
          title="Link Car"
          submitButtonText="Link Car"
        />
        <ConfirmModal
          show={showUnlinkModal}
          onHide={() => setShowUnlinkModal(false)}
          title="Confirm Unlink Car"
          body={
            <p>
              Are you sure you want to unlink car
              <strong>{selectedCarNumber}</strong>?
            </p>
          }
          onOkayPress={confirmUnlinkCar}
          submitting={false}
        />
      </>
    ),
    // formRoute,
    activityFilter,
  };
};

export const useAdvancesBalanceForm = ({ id }: TFormHookParams = {}) => {
  // Get existing advances balance data
  const { loadData, loading, data } = useGet();

  useEffect(() => {
    loadData({ api: API_ADVANCES_BALANCE });
  }, []);

  // Check if this is an edit operation (id exists)
  const isEdit = !!id;

  const sections: IFormSection[] = [
    {
      title: "E-Coupon Balance Details",
      fields: [
        {
          field_name: "phone_number",
          label: "Phone Number",
          type: "text",
          props: {
            required: true,
            placeholder: "Enter phone number (e.g., 256784306486)",
            pattern: "^256[0-9]{9}$",
          },
        },
        {
          field_name: "balance",
          label: "Balance Amount",
          type: "number",
          props: {
            required: true,
            disabled: isEdit,
            placeholder: "Enter balance amount",
            min: 0,
            step: 1,
          },
        },
        {
          field_name: "is_active",
          label: "Status",
          type: "select",
          props: {
            required: true,
            disabled: isEdit,
            options: [
              { value: true, label: "Active" },
              { value: false, label: "Inactive" },
            ],
          },
        },
        {
          field_name: "declaration",
          label: "Declaration",
          type: "select",
          props: {
            required: false,
            disabled: isEdit,
            options: [
              { value: false, label: "No" },
              { value: true, label: "Yes" },
            ],
          },
        },
      ],
    },
  ];

  const title = "E-Coupon Balance Management Report";
  const redirect = getRegister(URL_ADVANCES_BALANCE);

  return {
    sections,
    title,
    redirect,
    loading,
    data,
  };
};

export const AdvancesBalanceForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}: any) => {
  const { sections } = useAdvancesBalanceForm({
    form,
    updateFormValues: updateFormValue,
  });

  return (
    <div>
      {sections.map((section: IFormSection, index: number) => (
        <div key={index}>
          <h3>{section.title}</h3>
          <FormFields
            form={form}
            error={error}
            updateFormValue={updateFormValue}
            onChange={onChange}
            fields={section.fields}
          />
        </div>
      ))}
    </div>
  );
};
